import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExerciseCoachComposer from './ExerciseCoachComposer';
import ExerciseCoachMessageAttachments from './ExerciseCoachMessageAttachments';
import CoachAvatar from '../CoachAvatar';
import CoachReactionChip from '../CoachReactionChip';
import { getCoachReactionTypingSnapshot, subscribeCoachReactionTyping } from './coachPresence';
import { subscribeWorkoutCoachDiscussionRequests } from './workoutCoachDiscussionRequest';
import {
  buildWorkoutCoachDiscussion,
  buildWorkoutCoachScopeOptions,
  latestWorkoutCoachPresence,
  workoutCoachEventKey,
  workoutCoachScopeLabel,
} from './workoutCoachThreadPresentation';
import { alignCoachTimelineTarget } from '../coachHubScroll';
import useWorkoutCoachDockOwnership from './useWorkoutCoachDockOwnership';
import './WorkoutCoachDockOwnership.css';

const mountedWorkoutCoachDocks = new Set();
// Measured visible-review latency warrants faster reconciliation. Keep the
// same expanded attempt budget; collapsed pending previews use a finite read-only watch.
const GENERATION_POLL_INTERVAL_MS = 750;
const GENERATION_POLL_MAX_ATTEMPTS = 160;
const COLLAPSED_POLL_MS = 2000;
const READ_DEADLINE_MS = 15000;
// Neutral only with the backend's verified evidence-only subcode; any other
// terminal state, including an unqualified supersession, stays a failure.
const SAFE_SUPERSEDED_DETAIL = 'WORKOUT_SOURCE_SUPERSEDED';
const PROVIDER_FAILURE_COPY = Object.freeze({
  PROVIDER_MODEL_DEADLINE: 'Coach reached its response time limit.',
  PROVIDER_UPSTREAM_TIMEOUT: 'The model provider timed out.',
  PROVIDER_TOOL_ABORTED: 'Coach stopped during a tool operation.',
  PROVIDER_RATE_LIMITED: 'The model provider rate-limited this request.',
  PROVIDER_CONNECTION_FAILED: 'Coach could not connect to the model provider.',
  PROVIDER_UNKNOWN: 'The model provider failed.',
});
const TERMINAL_GENERATION_STATUSES = ['failed', 'cancelled', 'invalidated', 'timedout', 'superseded'];
const isSafeSupersession = generation => generation?.status === 'superseded'
  && generation?.detail_code === SAFE_SUPERSEDED_DETAIL;
// A reply to the user's own message is never a silent skip: it stays a
// failure with a way to send the unanswered message again.
const REPLY_SUPERSEDED_ERROR = 'WORKOUT_COACH_REPLY_SUPERSEDED';
const isReplySupersession = generation => generation?.status === 'failed' && generation?.error === REPLY_SUPERSEDED_ERROR;

function eventCoversExercise(event, workoutExerciseId) {
  const scope = event?.scope || {};
  return String(scope.workout_exercise_id || '') === workoutExerciseId
    || (Array.isArray(scope.workout_exercise_ids) && scope.workout_exercise_ids.map(String).includes(workoutExerciseId));
}

function unansweredMessage(events, generation) {
  const workoutExerciseId = String(generation?.workout_exercise_id || '');
  const taskCreatedAt = Date.parse(generation?.created_at || '');
  if (!workoutExerciseId) return '';
  const candidates = events.filter(event => event.type === 'user_message' && eventCoversExercise(event, workoutExerciseId)
    && (!Number.isFinite(taskCreatedAt) || !(Date.parse(event.created_at || '') > taskCreatedAt)));
  return String(candidates[candidates.length - 1]?.payload?.message || '').trim();
}
const expandedWorkoutCoachDocks = new Set();
const measuredWorkoutCoachDockHeights = new Map();

function syncWorkoutCoachBodyState() {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('workout-coach-dock-visible', mountedWorkoutCoachDocks.size > 0);
  document.body.classList.toggle('workout-coach-dock-expanded', expandedWorkoutCoachDocks.size > 0);
  const measuredHeight = Math.max(0, ...measuredWorkoutCoachDockHeights.values());
  if (measuredHeight > 0) document.body.style.setProperty('--workout-coach-dock-height', `${measuredHeight}px`);
  else document.body.style.removeProperty('--workout-coach-dock-height');
}

function eventIcon(kind, coachImageUrl, coachEmoji, coachIdentitySettled) {
  if (kind === 'coach') {
    return (
      <span className="workout-coach-dock-event-icon workout-coach-dock-event-avatar">
        <CoachAvatar
          imageUrl={coachImageUrl}
          emoji={coachEmoji}
          identitySettled={coachIdentitySettled}
          alt="Coach"
          imageClassName="workout-coach-dock-avatar"
        />
      </span>
    );
  }
  if (kind === 'user') return <span className="workout-coach-dock-event-icon">👤</span>;
  if (kind === 'set') return <span className="workout-coach-dock-event-icon">✓</span>;
  return <span className="workout-coach-dock-event-icon">•</span>;
}

function CollapsedCoachAvatar({ coachImageUrl, coachEmoji, coachIdentitySettled }) {
  return (
    <span className="workout-coach-dock-collapsed-icon is-speaking">
      <CoachAvatar
        imageUrl={coachImageUrl}
        emoji={coachEmoji}
        identitySettled={coachIdentitySettled}
        eager
      />
    </span>
  );
}

// Remount all conversation/draft state at the workout or authenticated service
// boundary; old async reads are fenced by the session's unmount cleanup.
function WorkoutCoachDock(props) {
  const [serviceScope, setServiceScope] = useState({ service: props.backendService, version: 0 });
  if (serviceScope.service !== props.backendService) {
    setServiceScope({ service: props.backendService, version: serviceScope.version + 1 });
  }
  return <WorkoutCoachDockSession key={`${props.workout?._id || ''}:${serviceScope.version}`} {...props} />;
}

function WorkoutCoachDockSession({
  workout,
  workoutExercises = [],
  exercises = [],
  selectedExerciseId,
  backendService,
  onSubmitMessage,
  onAnswerQuestion,
  recommendations = {},
  coachImageUrl = null,
  coachEmoji = '🥋',
  coachIdentitySettled = false,
  refreshVersion = 0,
  onGenerationSettled,
}) {
  const [expanded, setExpanded] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  const [needsRead, setNeedsRead] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorSource, setErrorSource] = useState('');
  const [notice, setNotice] = useState('');
  const [generation, setGeneration] = useState(null);
  const generationRef = useRef(null);
  const generationPollRef = useRef({ key: null, attempts: 0 });
  const readRecoveryRef = useRef({ failures: 0, denied: false, needed: false, lastResume: -Infinity });
  const cancelReadWaitRef = useRef(null);
  const onGenerationSettledRef = useRef(onGenerationSettled);
  onGenerationSettledRef.current = onGenerationSettled;
  const [entering, setEntering] = useState(true);
  const [pillPulseNonce, setPillPulseNonce] = useState(0);
  const [coachTyping, setCoachTyping] = useState(() => getCoachReactionTypingSnapshot());
  const [scopeId, setScopeId] = useState(() => selectedExerciseId || workoutExercises[0]?._id || '');
  const [discussionDraft, setDiscussionDraft] = useState(null);
  const [resendDraft, setResendDraft] = useState(null);
  const [focusedEventKey, setFocusedEventKey] = useState('');
  const [focusAnnouncement, setFocusAnnouncement] = useState('');
  const shellRef = useRef(null);
  const { active, activate } = useWorkoutCoachDockOwnership(shellRef);
  const pendingDiscussionFocusRef = useRef(false);
  useEffect(() => {
    if (!active || !expanded || !pendingDiscussionFocusRef.current) return undefined;
    let frame;
    // Wait for both ownership and the expanded surface to commit. In reduced
    // motion the composer can otherwise try to focus during the inert commit.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        shellRef.current?.querySelector('textarea')?.focus({ preventScroll: true });
        pendingDiscussionFocusRef.current = false;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [active, expanded, discussionDraft]);
  const historyRef = useRef(null);
  const eventRefs = useRef(new Map());
  const expandButtonRef = useRef(null);
  const collapseButtonRef = useRef(null);
  const requestVersionRef = useRef(0);
  const readControllerRef = useRef(null);
  const cancelRead = useCallback(() => {
    requestVersionRef.current += 1;
    if (readControllerRef.current) setNeedsRead(true);
    readControllerRef.current?.abort();
    cancelReadWaitRef.current?.();
    readControllerRef.current = null;
    setLoading(false);
  }, []);
  const followLatestRef = useRef(true);
  const hasOpenedRef = useRef(false);
  const focusFollowsToggleRef = useRef(false);
  const expandPointerIntentRef = useRef(false);
  const expandPointerIntentTimerRef = useRef(null);
  const expandedRef = useRef(expanded);
  const lastSelectedExerciseRef = useRef(null);
  const dockInstanceRef = useRef(Symbol('workout-coach-dock'));
  const lastPillAnimatedKeyRef = useRef(null);
  const presenceKeyAtCollapseRef = useRef(null);
  const pendingFocusEventKeyRef = useRef('');
  const focusHighlightTimerRef = useRef(null);
  const highlightedEventRef = useRef(null);
  const options = useMemo(() => buildWorkoutCoachScopeOptions(workoutExercises, exercises), [workoutExercises, exercises]);
  const validIds = useMemo(() => new Set(options.map(option => option.id)), [options]);
  const updateDockHeight = useCallback(() => {
    const height = Math.ceil(shellRef.current?.getBoundingClientRect().height || 0);
    if (active && height > 0) measuredWorkoutCoachDockHeights.set(dockInstanceRef.current, height);
    else measuredWorkoutCoachDockHeights.delete(dockInstanceRef.current);
    syncWorkoutCoachBodyState();
  }, [active]);

  useEffect(() => {
    if (options.length === 0) return;
    const selectedKey = String(selectedExerciseId || '');
    const selectedChanged = selectedKey !== lastSelectedExerciseRef.current;
    if (selectedChanged && lastSelectedExerciseRef.current !== null) cancelRead();
    const currentIsValid = validIds.has(String(scopeId));
    if (!selectedChanged && currentIsValid) return;

    const selectedOption = options.find(option => option.memberIds.includes(selectedKey));
    const preferred = selectedOption?.id || (currentIsValid ? String(scopeId) : String(options[0].id));
    lastSelectedExerciseRef.current = selectedKey;
    if (preferred !== scopeId) setScopeId(preferred);
  }, [selectedExerciseId, validIds, options, scopeId, cancelRead]);

  useEffect(() => subscribeWorkoutCoachDiscussionRequests(request => {
    if (String(request?.workoutId || '') !== String(workout?._id || '')) return;
    const targetOption = options.find(option => option.memberIds.includes(String(request.workoutExerciseId)));
    if (!targetOption) return;
    pendingDiscussionFocusRef.current = true;
    activate();
    focusFollowsToggleRef.current = false;
    setScopeId(String(targetOption.id));
    setExpanded(true);
    setDiscussionDraft({ key: request.key, text: request.draftText });
    setResendDraft(null);
  }), [options, workout?._id, activate]);

  const refreshThread = useCallback(async ({ throwOnError = false, supersede = false } = {}) => {
    if (document.visibilityState === 'hidden' || navigator.onLine === false) {
      readRecoveryRef.current.needed = true;
      setNeedsRead(true);
      setLoading(false);
      return false;
    }
    if (readControllerRef.current && !supersede) return false;
    if (readControllerRef.current) {
      readControllerRef.current.abort();
      cancelReadWaitRef.current?.();
    }
    if (!workout?._id || !backendService?.getWorkoutCoachThread) {
      setLoading(false);
      setErrorSource('fetch');
      setError('Could not load Workout Coach.');
      return false;
    }
    const requestVersion = ++requestVersionRef.current;
    readControllerRef.current?.abort();
    const readController = new AbortController();
    readControllerRef.current = readController;
    setNeedsRead(false);
    setLoading(true);
    setError('');
    setErrorSource('');
    setNotice('');
    let timeout;
    try {
      const result = await Promise.race([
        Promise.resolve().then(() => {
          if (readController.signal.aborted) throw new Error('cancelled');
          return backendService.getWorkoutCoachThread(workout._id, readController.signal);
        }),
        new Promise((_, reject) => {
          cancelReadWaitRef.current = () => reject(new Error('cancelled'));
          timeout = setTimeout(() => { readController.abort(); reject(new Error('Could not load Workout Coach in time.')); }, READ_DEADLINE_MS);
        }),
      ]);
      if (requestVersion !== requestVersionRef.current) {
        if (throwOnError) throw new Error('Workout Coach refresh was superseded before canonical state was accepted.');
        return false;
      }
      setEvents(Array.isArray(result?.events) ? result.events : []);
      readRecoveryRef.current.needed = false;
      readRecoveryRef.current.denied = false;
      const next = result?.generation || null;
      const previouslyPending = ['pending', 'offline'].includes(generationRef.current?.status);
      generationRef.current = next;
      setGeneration(next);
      if (isSafeSupersession(next)) {
        setNotice('Coach skipped an outdated review because your workout changed before it finished. It was not retried.');
      } else if (isReplySupersession(next)) {
        setErrorSource('generation');
        setError('Coach could not answer your message because your workout changed before the reply was ready. Send it again to get an answer.');
      } else if (next && TERMINAL_GENERATION_STATUSES.includes(next.status)) {
        setErrorSource('generation');
        const detail = next.failure_code === 'TASK_PROVIDER_FAILED'
          && Object.prototype.hasOwnProperty.call(PROVIDER_FAILURE_COPY, next.detail_code)
          ? PROVIDER_FAILURE_COPY[next.detail_code] : '';
        setError(`${detail || 'The selected Coach could not finish this response.'} No fallback Coach was used.`);
      } else if (next?.status === 'offline') {
        setErrorSource('generation');
        setError('The external Coach worker is stopped or not checking for tasks. Start it for new feedback. Refresh only checks this saved task while it is still within its deadline; it does not restart the worker.');
      }
      if (previouslyPending && next?.status === 'consumed') {
        Promise.resolve(onGenerationSettledRef.current?.()).catch(() => {});
      }
      return true;
    } catch (fetchError) {
      if (requestVersion !== requestVersionRef.current) {
        if (throwOnError) throw fetchError;
        return false;
      }
      setErrorSource('fetch');
      readRecoveryRef.current.failures += 1;
      readRecoveryRef.current.denied = [401, 403].includes(fetchError?.response?.status ?? fetchError?.status);
      readRecoveryRef.current.needed = !readRecoveryRef.current.denied;
      setError(fetchError?.message || 'Could not load Workout Coach.');
      if (throwOnError) throw fetchError;
      return false;
    } finally {
      clearTimeout(timeout);
      if (requestVersion === requestVersionRef.current) {
        cancelReadWaitRef.current = null;
        readControllerRef.current = null;
        setLoading(false);
      }
    }
  }, [backendService, workout?._id]);

  useEffect(() => {
    refreshThread({ supersede: true });
  }, [refreshThread, refreshVersion]);

  useEffect(() => {
    const onVisibility = () => {
      const visible = document.visibilityState !== 'hidden' && navigator.onLine !== false;
      if (!visible) cancelRead();
      setPageVisible(visible);
      const recovery = readRecoveryRef.current;
      if (visible && !recovery.denied && (recovery.needed || generationRef.current?.status === 'pending')
        && Date.now() - recovery.lastResume >= COLLAPSED_POLL_MS && !readControllerRef.current) {
        recovery.lastResume = Date.now();
        refreshThread();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    ['focus', 'pageshow', 'online', 'offline'].forEach(event => window.addEventListener(event, onVisibility));
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      ['focus', 'pageshow', 'online', 'offline'].forEach(event => window.removeEventListener(event, onVisibility));
    };
  }, [cancelRead, refreshThread]);

  // A cancelled first read has no generation to drive the pending poll. Resume
  // once on the visible surface, including its collapsed saved-message preview.
  useEffect(() => {
    if (needsRead && active && pageVisible) refreshThread();
  }, [needsRead, active, expanded, pageVisible, scopeId, refreshThread]);

  useEffect(() => {
    if (!active || !pageVisible || loading || errorSource === 'fetch' || generation?.status !== 'pending') return undefined;
    const key = `${workout?._id}:${generation.task_id}`;
    if (generationPollRef.current.key !== key) generationPollRef.current = { key, attempts: 0 };
    if (generationPollRef.current.attempts >= GENERATION_POLL_MAX_ATTEMPTS) {
      setGeneration(current => current?.task_id === generation.task_id ? { ...current, status: 'paused' } : current);
      setErrorSource('generation');
      setError('The selected Coach has not replied yet. If you use an external Coach, check that its worker is running. Refresh checks the saved task; it does not restart the worker.');
      return undefined;
    }
    const timer = window.setTimeout(() => {
      generationPollRef.current.attempts += 1;
      refreshThread();
    }, expanded ? GENERATION_POLL_INTERVAL_MS : COLLAPSED_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [generation, refreshThread, workout?._id, active, expanded, pageVisible, scopeId, loading, errorSource]);

  useEffect(() => {
    const recovery = readRecoveryRef.current;
    if (!active || !pageVisible || loading || errorSource !== 'fetch' || recovery.denied || !recovery.needed || recovery.failures > 4) return undefined;
    const timer = setTimeout(() => refreshThread(), COLLAPSED_POLL_MS * (2 ** (recovery.failures - 1)));
    return () => clearTimeout(timer);
  }, [active, pageVisible, loading, errorSource, refreshThread]);

  useEffect(() => subscribeCoachReactionTyping(setCoachTyping), []);

  useEffect(() => () => {
    requestVersionRef.current += 1;
    readControllerRef.current?.abort();
    cancelReadWaitRef.current?.();
  }, []);

  useEffect(() => () => {
    if (expandPointerIntentTimerRef.current) {
      window.clearTimeout(expandPointerIntentTimerRef.current);
    }
    if (focusHighlightTimerRef.current) {
      window.clearTimeout(focusHighlightTimerRef.current);
    }
    highlightedEventRef.current?.classList.remove('is-focus-target');
  }, []);

  useEffect(() => {
    const instance = dockInstanceRef.current;
    if (!active) return undefined;
    mountedWorkoutCoachDocks.add(instance);
    syncWorkoutCoachBodyState();
    const entryTimer = window.setTimeout(() => setEntering(false), 900);
    return () => {
      window.clearTimeout(entryTimer);
      mountedWorkoutCoachDocks.delete(instance);
      expandedWorkoutCoachDocks.delete(instance);
      measuredWorkoutCoachDockHeights.delete(instance);
      syncWorkoutCoachBodyState();
    };
  }, [active]);

  useEffect(() => {
    const frame = requestAnimationFrame(updateDockHeight);
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateDockHeight)
      : null;
    if (shellRef.current) resizeObserver?.observe(shellRef.current);
    window.addEventListener('resize', updateDockHeight);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateDockHeight);
    };
  }, [updateDockHeight]);

  useEffect(() => {
    const instance = dockInstanceRef.current;
    if (active && expanded) expandedWorkoutCoachDocks.add(instance);
    else expandedWorkoutCoachDocks.delete(instance);
    syncWorkoutCoachBodyState();
  }, [active, expanded]);

  useEffect(() => {
    expandedRef.current = expanded;
    if (!focusFollowsToggleRef.current) return;
    (expanded ? collapseButtonRef : expandButtonRef).current?.focus();
  }, [expanded]);

  const scrollHistoryToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const history = historyRef.current;
      if (history) history.scrollTop = Math.max(0, history.scrollHeight - history.clientHeight);
    });
  }, []);

  useEffect(() => {
    if (!expanded || hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    if (!pendingFocusEventKeyRef.current) scrollHistoryToBottom();
  }, [expanded, scrollHistoryToBottom]);

  useEffect(() => {
    if (expandedRef.current && followLatestRef.current) scrollHistoryToBottom();
  }, [events.length, scrollHistoryToBottom]);

  const handleHistoryScroll = () => {
    const history = historyRef.current;
    if (!history) return;
    followLatestRef.current = history.scrollHeight - history.scrollTop - history.clientHeight < 32;
  };

  const latestPresence = useMemo(() => latestWorkoutCoachPresence(events), [events]);
  const latestCoachText = latestPresence.text;
  const showTypingCue = !error && (coachTyping || generation?.status === 'pending');
  const collapsedCueLabel = loading
    ? (events.length ? 'Refreshing Workout Coach conversation' : 'Loading Workout Coach conversation')
    : (showTypingCue ? 'Workout Coach is typing' : '');
  const collapsedReaction = showTypingCue ? null : latestPresence.reaction;
  const latestCoachKey = latestPresence.key;
  const pillVisualKey = `${latestCoachKey}:${pillPulseNonce}`;

  useEffect(() => {
    if (expanded && showTypingCue && followLatestRef.current) scrollHistoryToBottom();
  }, [expanded, showTypingCue, scrollHistoryToBottom]);

  useEffect(() => {
    const frame = requestAnimationFrame(updateDockHeight);
    return () => cancelAnimationFrame(frame);
  }, [latestCoachText, collapsedCueLabel, error, expanded, updateDockHeight]);

  useEffect(() => {
    if (expanded || lastPillAnimatedKeyRef.current === latestCoachKey) return;
    lastPillAnimatedKeyRef.current = latestCoachKey;
    setPillPulseNonce(value => value + 1);
  }, [expanded, latestCoachKey]);
  const exerciseMetadataById = useMemo(() => Object.fromEntries(workoutExercises.map(item => [String(item._id),
    exercises.find(exercise => String(exercise.id || exercise._id) === String(item.exercise_id)),
  ])), [workoutExercises, exercises]);
  const presentedEvents = useMemo(() => buildWorkoutCoachDiscussion(events, exerciseMetadataById), [events, exerciseMetadataById]);
  const generationEndedAt = Date.parse(generation?.terminal_at || '');
  const hasLaterActivity = Number.isFinite(generationEndedAt) && events.some(event => {
    if (!Number.isFinite(Date.parse(event.created_at || '')) || Date.parse(event.created_at) <= generationEndedAt) return false;
    // A later set is not an answer to an unanswered question. Keep its resend
    // action until this exercise has a newer user turn, not an unrelated event.
    if (isReplySupersession(generation)) return event.type === 'user_message'
      && String(event.scope?.workout_exercise_id || '') === String(generation.workout_exercise_id || '');
    return true;
  });
  const isGenerationNotice = errorSource === 'generation';
  const terminalGeneration = TERMINAL_GENERATION_STATUSES.includes(generation?.status);
  const visibleError = error && !(isGenerationNotice && terminalGeneration && hasLaterActivity) ? error : '';
  const visibleNotice = !visibleError && notice && !hasLaterActivity ? notice : '';
  const collapsedNotice = visibleError
    ? (errorSource === 'fetch' ? 'Could not load Coach conversation.'
      : generation?.status === 'offline' ? 'Coach is offline.' : "Review didn't finish")
    : (visibleNotice ? 'Outdated Coach review skipped.' : '');
  // This is a task receipt, not a claim that each visible set was reviewed.
  const silentReview = generation?.status === 'consumed'
    && generation?.review?.reactions === 0 && generation?.review?.reply === false;
  const reviewStatus = silentReview && !hasLaterActivity ? 'Review complete · No reaction' : '';
  const collapsedCoachText = latestCoachText || (!collapsedCueLabel ? (collapsedNotice || reviewStatus || 'No Coach messages yet.') : '');
  const generationScope = options.find(option => option.memberIds.includes(String(generation?.workout_exercise_id || '')))?.label;
  const errorContext = (isGenerationNotice || visibleNotice) && generation?.task_id
    ? `Coach response to ${generationScope || 'workout start'}: ` : '';
  const resendText = visibleError && isGenerationNotice && isReplySupersession(generation) ? unansweredMessage(events, generation) : '';
  const resendUnansweredMessage = () => {
    const targetOption = options.find(option => option.memberIds.includes(String(generation?.workout_exercise_id || '')));
    if (targetOption) setScopeId(String(targetOption.id));
    setDiscussionDraft(null);
    setResendDraft({ key: `resend:${generation?.task_id || ''}:${Date.now()}`, text: resendText });
  };

  useEffect(() => {
    const focusEventKey = pendingFocusEventKeyRef.current;
    if (!expanded || !focusEventKey) return undefined;
    let secondFrame = null;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const target = eventRefs.current.get(focusEventKey);
        const history = historyRef.current;
        if (!target || !history) return;
        alignCoachTimelineTarget(history, target, 8);
        followLatestRef.current = false;
        highlightedEventRef.current?.classList.remove('is-focus-target');
        target.classList.add('is-focus-target');
        highlightedEventRef.current = target;
        setFocusedEventKey(focusEventKey);
        const focusedEvent = presentedEvents.find(item => workoutCoachEventKey(item.event) === focusEventKey);
        setFocusAnnouncement(focusedEvent
          ? `Showing ${focusedEvent.presentation.scopeLabel}: ${focusedEvent.presentation.text}`
          : 'Showing the selected Workout Coach message');
        pendingFocusEventKeyRef.current = '';
        if (focusHighlightTimerRef.current) window.clearTimeout(focusHighlightTimerRef.current);
        focusHighlightTimerRef.current = window.setTimeout(() => {
          target.classList.remove('is-focus-target');
          if (highlightedEventRef.current === target) highlightedEventRef.current = null;
          focusHighlightTimerRef.current = null;
        }, 2400);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) cancelAnimationFrame(secondFrame);
    };
  }, [expanded, presentedEvents]);

  // Approval controls live only in the owning exercise UI. Keep the dock
  // available for conversation, including questions about those suggestions.
  const selectedOption = options.find(option => option.id === scopeId);
  const selectedRecommendation = recommendations?.[scopeId]
    || selectedOption?.memberIds.map(id => recommendations?.[id]).find(Boolean)
    || null;
  const pendingQuestion = selectedRecommendation?.question;

  const submitMessage = async (message, attachments) => {
    if (!active) throw new Error('Open this workout Coach before sending.');
    if (!scopeId || !onSubmitMessage) throw new Error('Select an exercise before messaging Coach.');
    if (pendingQuestion && !discussionDraft && attachments.length === 0 && message.trim() && onAnswerQuestion) {
      await onAnswerQuestion(scopeId, message.trim());
      await refreshThread({ supersede: true });
      return;
    }
    try {
      await onSubmitMessage(scopeId, message, attachments);
      await refreshThread({ supersede: true });
      setDiscussionDraft(null);
      setResendDraft(null);
    } catch (submitError) {
      if (submitError?.messageSaved) await refreshThread({ supersede: true });
      throw submitError;
    }
  };

  const answerQuestion = async (answer) => {
    if (!active || !scopeId || !onAnswerQuestion) return;
    await onAnswerQuestion(scopeId, answer);
    await refreshThread({ supersede: true });
  };

  const disarmExpandPointerIntent = () => {
    if (expandPointerIntentTimerRef.current) {
      window.clearTimeout(expandPointerIntentTimerRef.current);
      expandPointerIntentTimerRef.current = null;
    }
    expandPointerIntentRef.current = false;
  };

  const armExpandPointerIntent = () => {
    disarmExpandPointerIntent();
    expandPointerIntentRef.current = true;
    expandPointerIntentTimerRef.current = window.setTimeout(() => {
      expandPointerIntentRef.current = false;
      expandPointerIntentTimerRef.current = null;
    }, 1500);
  };

  const expandDock = (event) => {
    if (!active) return;
    const pointerActivated = Number(event?.detail || 0) > 0;
    if (pointerActivated && !expandPointerIntentRef.current) return;
    disarmExpandPointerIntent();
    focusFollowsToggleRef.current = true;
    pendingFocusEventKeyRef.current = latestPresence.focusEventKey || '';
    const presenceChangedWhileCollapsed = presenceKeyAtCollapseRef.current !== latestPresence.key;
    if (presenceChangedWhileCollapsed && latestPresence.focusWorkoutExerciseId) {
      const targetOption = options.find(option => option.memberIds.includes(String(latestPresence.focusWorkoutExerciseId)));
      if (targetOption) setScopeId(String(targetOption.id));
    }
    setExpanded(true);
  };

  const collapseDock = () => {
    cancelRead();
    focusFollowsToggleRef.current = true;
    presenceKeyAtCollapseRef.current = latestPresence.key;
    setExpanded(false);
  };
  const collapsedAriaLabel = [
    collapsedReaction
      ? `Expand Workout Coach. Coach reacted: ${collapsedReaction.label || collapsedReaction.key || 'Reaction'}. ${collapsedCoachText}`
      : 'Expand Workout Coach',
    collapsedCueLabel,
    collapsedNotice,
  ].filter(Boolean).join('. ');

  return (
    <aside ref={shellRef} data-floating-control="coach" data-coach-active={active} inert={!active ? '' : undefined} aria-hidden={!active} className={`workout-coach-dock-shell ${expanded ? 'is-expanded' : 'is-collapsed'}${entering ? ' is-entering' : ''}`} aria-label="Workout Coach conversation">
        <button
          ref={expandButtonRef}
          type="button"
          className="workout-coach-dock-collapsed is-message-fresh"
          aria-label={collapsedAriaLabel}
          aria-expanded={expanded}
          aria-hidden={expanded}
          inert={expanded ? '' : undefined}
          tabIndex={expanded ? -1 : 0}
          onPointerDown={armExpandPointerIntent}
          onPointerCancel={disarmExpandPointerIntent}
          onClick={expandDock}
        >
          <CollapsedCoachAvatar coachImageUrl={coachImageUrl} coachEmoji={coachEmoji} coachIdentitySettled={coachIdentitySettled} />
          <span className="workout-coach-dock-collapsed-copy">
            {collapsedReaction && (
              <span
                className={`workout-coach-dock-collapsed-reaction sentiment-${collapsedReaction.sentiment || 'neutral'}`}
                role="img"
                aria-label={`Workout Coach reacted: ${collapsedReaction.label || collapsedReaction.key || 'Reaction'}`}
              >
                <span aria-hidden="true">{collapsedReaction.emoji}</span>
                <span>{collapsedReaction.label || collapsedReaction.key}</span>
              </span>
            )}
            {collapsedCoachText && (
              <span key={pillVisualKey} data-message-key={latestCoachKey} className="workout-coach-dock-latest-message" aria-live={!active || expanded ? 'off' : 'polite'} aria-atomic="true">{collapsedCoachText}</span>
            )}
            {latestCoachText && reviewStatus && <span className="workout-coach-dock-status-notice is-neutral" role="status">{reviewStatus}</span>}
            {latestCoachText && collapsedNotice && (
              <span className={`workout-coach-dock-status-notice${visibleNotice ? ' is-neutral' : ''}`} role="status">{collapsedNotice}</span>
            )}
            {collapsedCueLabel && !expanded && (
              <span className="workout-coach-dock-typing-cue" aria-label={collapsedCueLabel} role="status">
                <span className="workout-coach-dock-typing-dot" aria-hidden="true" />
                <span className="workout-coach-dock-typing-dot" aria-hidden="true" />
                <span className="workout-coach-dock-typing-dot" aria-hidden="true" />
              </span>
            )}
          </span>
          <span className="workout-coach-dock-expand-mark">⌃</span>
        </button>

      <div className="workout-coach-dock" role="region" aria-label="Expanded Workout Coach" aria-hidden={!expanded} inert={!expanded ? '' : undefined}>
        <button ref={collapseButtonRef} type="button" className="workout-coach-dock-header" aria-label="Collapse Workout Coach" onClick={collapseDock}>
          <span className="workout-coach-dock-identity">
            <span className="workout-coach-dock-header-icon">
              <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled={coachIdentitySettled} alt="Coach" eager />
            </span>
            <span className="workout-coach-dock-identity-copy">
              <strong>Workout Coach</strong>
              <span>Ongoing workout</span>
            </span>
          </span>
          <span className="workout-coach-dock-collapse" aria-hidden="true">⌄</span>
        </button>

        <label className="workout-coach-dock-scope">
          <span>Message about</span>
          <select aria-label="Coach message exercise scope" value={scopeId} onChange={event => { cancelRead(); setScopeId(event.target.value); }}>
            {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>

        <div className="workout-coach-dock-history" ref={historyRef} onScroll={handleHistoryScroll}>
          <span className="workout-coach-dock-focus-status" role="status" aria-live="polite">{focusAnnouncement}</span>
          {loading && events.length === 0 && <div className="workout-coach-dock-empty" role="status">Loading Workout Coach conversation…</div>}
          {!loading && events.length === 0 && !reviewStatus && !visibleError && !visibleNotice && !showTypingCue && <div className="workout-coach-dock-empty">No Coach messages yet. Select an exercise and ask anything.</div>}
          {presentedEvents.map(({ event, presentation }) => {
            const eventKey = workoutCoachEventKey(event);
            return (
              <div
                key={eventKey}
                ref={node => {
                  if (node) eventRefs.current.set(eventKey, node);
                  else eventRefs.current.delete(eventKey);
                }}
                data-workout-coach-event-key={eventKey}
                aria-current={focusedEventKey === eventKey ? 'true' : undefined}
                className={`workout-coach-dock-event workout-coach-dock-event-${presentation.kind}${presentation.reaction ? ' has-reaction' : ''}`}
              >
                {presentation.side !== 'user' && eventIcon(presentation.kind, coachImageUrl, coachEmoji, coachIdentitySettled)}
                <div className="workout-coach-dock-event-body">
                  <span className="workout-coach-dock-scope-badge">{presentation.scopeLabel || workoutCoachScopeLabel(event.scope)}</span>
                  <span className="workout-coach-dock-event-text">{presentation.text}</span>
                  {presentation.reaction && (
                    <CoachReactionChip reaction={presentation.reaction} coachName="Workout Coach" />
                  )}
                  {presentation.attachments?.length > 0 && (
                    <ExerciseCoachMessageAttachments attachments={presentation.attachments} backendService={backendService} />
                  )}
                </div>
              </div>
            );
          })}
          {showTypingCue && expanded && !visibleError && (
            <div className="workout-coach-dock-event workout-coach-dock-event-typing" role="status" aria-label="Workout Coach is typing">
              {eventIcon('coach', coachImageUrl, coachEmoji, coachIdentitySettled)}
              <div className="workout-coach-dock-event-body">
                <span className="workout-coach-dock-typing-cue" aria-hidden="true">
                  <span className="workout-coach-dock-typing-dot" />
                  <span className="workout-coach-dock-typing-dot" />
                  <span className="workout-coach-dock-typing-dot" />
                </span>
              </div>
            </div>
          )}
          {reviewStatus && <div className="workout-coach-dock-notice" role="status">{reviewStatus}</div>}
          {visibleNotice && <div className="workout-coach-dock-notice" role="status">{errorContext}{visibleNotice}</div>}
          {visibleError && <div className="workout-coach-dock-error" role="alert">{errorContext}{visibleError}{(!isGenerationNotice || ['offline', 'paused'].includes(generation?.status)) && <> <button type="button" onClick={() => { generationPollRef.current.attempts = 0; refreshThread(); }}>Refresh</button></>}{resendText && <> <button type="button" onClick={resendUnansweredMessage}>Send again</button></>}</div>}
        </div>

        {pendingQuestion && (
          <div className="workout-coach-dock-question">
            <span>{pendingQuestion}</span>
            <div className="workout-coach-dock-quick-answers">
              {selectedRecommendation.question_type === 'yes_no' && ['Yes', 'No'].map(answer => (
                <button type="button" key={answer} onClick={() => answerQuestion(answer)}>{answer}</button>
              ))}
              {selectedRecommendation.question_type === 'choice' && (selectedRecommendation.question_choices || []).map(answer => (
                <button type="button" key={answer} onClick={() => answerQuestion(answer)}>{answer}</button>
              ))}
              <button type="button" aria-label="Skip Coach question" onClick={() => answerQuestion('[skipped]')}>Skip</button>
            </div>
          </div>
        )}

        <div className="workout-coach-dock-composer">
          <ExerciseCoachComposer
            onSubmit={submitMessage}
            disabled={!active || !scopeId}
            placeholder={discussionDraft ? 'Ask about this suggested change…' : 'Ask Workout Coach…'}
            resetKey={`${workout._id}:${scopeId}`}
            draftRequest={discussionDraft || resendDraft}
          />
        </div>
      </div>
    </aside>
  );
}

export default WorkoutCoachDock;
